import { Button } from '@augmentt-dev/ui-components'

import { isString } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { Alert, Modal } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import Pill from '../Pill'
import StepModalHeader from '../StepModalHeader'
import TabSelector from '../TabSelector'
import { useTabContext } from './TabContext'

const propTypes = {
  /**
   * Title of the modal
   * If a string is passed it needs to be a FormattedMessage id
   * If a react node is passed render it the way it is
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * If present, will render a pill with the label passed. FormattedMessage id
   */
  titlePillLabel: PropTypes.string,
  /**
   * Subtitles of the modal
   */
  subTitles: PropTypes.arrayOf(PropTypes.node),
  /**
   * If true, show error banner in header
   */
  showValidationErrorBanner: PropTypes.bool,
  /**
   * Color variant of the header steps
   */
  variant: PropTypes.oneOf(['primary', 'engage']),
  /**
   * Steps array: each step should contain a label and a key
   * If passed display the steps on the header
   */
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      key: PropTypes.string,
    }),
  ),
  /**
   * Indicates which of the steps are active
   */
  currentStepIndex: PropTypes.number,
  /**
   * Label of the back button. FormattedMessage id
   */
  backButtonLabel: PropTypes.string,
  /**
   * Function triggered on back button click
   */
  backButtonAction: PropTypes.func,
  /**
   * Rendered right after the tabs (see ModalPanel tabsAddon)
   */
  tabsAddon: PropTypes.node,
}

function Header(props) {
  const {
    title,
    subTitles = [],
    showValidationErrorBanner = false,
    variant = 'primary',
    steps = [],
    currentStepIndex = 0,
    backButtonLabel = null,
    backButtonAction = null,
    titlePillLabel = null,
    tabsAddon = null,
  } = props ?? {}

  const { tabs, activeTab, setActiveTab } = useTabContext()

  return (
    <Modal.Header closeButton>
      {backButtonLabel && (
        <Button
          variant="link"
          className="p-0 mb-2"
          onClick={backButtonAction}
          style={{ color: 'var(--bs-body-color)' }}
        >
          <i className="fas fa-chevron-left me-1" />
          <FormattedMessage id={backButtonLabel} />
        </Button>
      )}

      <Modal.Title>
        {isString(title) ? <FormattedMessage id={title} /> : title}
        {titlePillLabel && (
          <Pill variant="primary" size="large" withMargins={false} className="ms-3">
            <FormattedMessage id={titlePillLabel} />
          </Pill>
        )}
      </Modal.Title>

      {subTitles?.map((subTitle, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={index} className="mt-2 mb-2 dark-text">
          {subTitle}
        </p>
      ))}

      {steps?.length ? (
        <div className="pb-2">
          <StepModalHeader steps={steps} stepIndex={currentStepIndex} variant={variant} />
        </div>
      ) : null}

      {tabs?.length && activeTab ? (
        <div className="pt-1">
          <TabSelector tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} addon={tabsAddon} />
        </div>
      ) : null}

      {showValidationErrorBanner && (
        <Alert variant="danger" className="d-flex align-items-center mb-0 mt-3">
          <i className="fas fa-triangle-exclamation me-2" />
          <FormattedMessage id="GENERAL.MODAL_VALIDATION_FAILED" />
        </Alert>
      )}
    </Modal.Header>
  )
}

Header.propTypes = propTypes

export default Header
